import { TextalignCenter, TextalignLeft, TextalignRight, Grid1 } from '@/components/icons';
import { Checkbox } from '@/components/ui/checkbox';
import CollapsibleSection from '@/components/ui/collapsible-section';
import { Label } from '@/components/ui/label';
import { summarizeTable } from '../summarize';
import type { TableCustomizations } from '@/components/preview/types';

interface TableSectionProps {
    open: boolean;
    onToggle: () => void;
    config: TableCustomizations;
    updateConfig: (updates: Partial<TableCustomizations>) => void;
    /** How many `table` blocks this laudo actually has. */
    blockCount: number;
}

/**
 * Layout of the test tables. The header colour moved to the Cores section, where
 * every colour of the laudo now lives.
 */
export function TableSection({
    open,
    onToggle,
    config,
    updateConfig,
    blockCount,
}: TableSectionProps) {
    return (
        <CollapsibleSection
            title="Tabelas"
            testId="customize-section-table"
            icon={Grid1}
            summary={summarizeTable(config)}
            muted={blockCount === 0}
            open={open}
            onToggle={onToggle}
        >
            {blockCount === 0 && (
                <p className="text-xs text-muted-foreground">
                    Este laudo ainda não tem tabelas. O ajuste vale para as que você adicionar — e
                    para o modelo, se salvar um.
                </p>
            )}

            {/* Text Alignment */}
            <div className="space-y-2">
                <Label className="text-sm font-medium">Alinhamento do Texto</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                    {(
                        [
                            {
                                value: 'left' as const,
                                label: 'Esquerda',
                                Icon: TextalignLeft,
                            },
                            {
                                value: 'center' as const,
                                label: 'Centro',
                                Icon: TextalignCenter,
                            },
                            {
                                value: 'right' as const,
                                label: 'Direita',
                                Icon: TextalignRight,
                            },
                        ] as const
                    ).map(({ value, label, Icon }) => (
                        <button
                            key={value}
                            onClick={() =>
                                updateConfig({
                                    text_alignment: value,
                                })
                            }
                            className={`flex flex-col items-center gap-2 rounded-lg border-2 p-3 transition-all ${
                                (config.text_alignment || 'center') === value
                                    ? 'border-[#00006B] bg-[#00006B] text-white'
                                    : 'border-border bg-card text-foreground hover:border-border-strong'
                            }`}
                        >
                            <Icon className="size-4" />
                            <span className="text-xs font-medium">{label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Colorir classificações */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="table-color-classifications"
                    checked={config.color_classifications !== false}
                    onCheckedChange={(checked) =>
                        updateConfig({ color_classifications: !!checked })
                    }
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="table-color-classifications"
                        className="cursor-pointer text-sm font-medium"
                    >
                        Colorir a coluna Classificação
                    </Label>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        Muito Inferior a Muito Superior ganham vermelho ou verde. Alto, Baixo e
                        Clínico ficam na cor padrão, porque a direção depende do instrumento.
                    </p>
                </div>
            </div>
        </CollapsibleSection>
    );
}
